import { computeTotals, formatEur, lineTotalCents } from '@aviaconsole/shared';
import { useI18n } from '../i18n';
import { Button } from './ui';

export interface EditableLine { description: string; quantity: number; unitPriceCents: number; vatRate: number; discount: boolean }

export function emptyLine(vatRate = 21): EditableLine { return { description: '', quantity: 1, unitPriceCents: 0, vatRate, discount: false }; }

function parseMoney(s: string): number { const n = Number(s.replace(/\./g, '').replace(',', '.')); return Number.isFinite(n) ? Math.round(n * 100) : 0; }
function money(c: number): string { return (c / 100).toFixed(2).replace('.', ','); }

/** Editable invoice/schedule lines with live totals (VAT rounded per line). */
export function LinesEditor({ lines, onChange, vatLocked, vatRate }: { lines: EditableLine[]; onChange: (l: EditableLine[]) => void; vatLocked?: boolean; vatRate?: number }) {
  const { t } = useI18n();
  const totals = computeTotals(lines);
  const update = (i: number, patch: Partial<EditableLine>) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <div>
      <div className="lines-scroll">
        <div className="lines-grid head"><span>{t('colQty')}</span><span>{t('colDescription')}</span><span style={{ textAlign: 'right' }}>{t('colUnitExcl')}</span><span>{t('colVatType')}</span><span style={{ textAlign: 'right' }}>{t('colTotal')}</span><span /></div>
        <div className="stack sm">
          {lines.map((l, i) => (
            <div key={i} className={`lines-grid rowline ${l.discount ? 'discount' : ''}`}>
              <input className="num" type="number" step="0.01" min="0" value={l.quantity} onChange={(e) => update(i, { quantity: Number(e.target.value) })} aria-label={t('quantity')} />
              <div className="row" style={{ flexWrap: 'nowrap' }}>
                <input value={l.description} placeholder={t('description')} onChange={(e) => update(i, { description: e.target.value })} aria-label={t('description')} />
                {l.discount && <span className="chip warning">{t('discount')}</span>}
              </div>
              <input className="num" defaultValue={money(l.unitPriceCents)} key={`u${i}-${l.unitPriceCents}`} onBlur={(e) => update(i, { unitPriceCents: parseMoney(e.target.value) })} aria-label={t('unitPrice')} />
              <input className="num" type="number" step="1" min="0" max="100" value={l.vatRate} disabled={vatLocked} onChange={(e) => update(i, { vatRate: Number(e.target.value) })} aria-label={t('vatRate')} />
              <span className="amount">{formatEur(lineTotalCents(l))}</span>
              <button type="button" className="linkbtn" aria-label={t('delete')} onClick={() => onChange(lines.filter((_, j) => j !== i))}><i className="fas fa-trash-can" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="row" style={{ marginTop: '0.8rem' }}>
        <Button size="sm" icon="fas fa-plus" onClick={() => onChange([...lines, emptyLine(vatRate)])}>{t('newLine')}</Button>
        <Button size="sm" icon="fas fa-percentage" onClick={() => onChange([...lines, { ...emptyLine(vatRate), description: t('discount'), discount: true }])}>{t('discount')}</Button>
      </div>
      <div className="totals">
        {totals.discountCents > 0 && <div><div className="label">{t('discountExcl')}</div><div className="v">{formatEur(totals.discountCents)}</div></div>}
        <div><div className="label">{t('subtotalExcl')}</div><div className="v">{formatEur(totals.subtotalCents)}</div></div>
        <div><div className="label">{t('vat')}</div><div className="v">{formatEur(totals.vatCents)}</div></div>
        <div><div className="label">{t('totalIncl')}</div><div className="v">{formatEur(totals.totalCents)}</div></div>
      </div>
    </div>
  );
}
